import type React from 'react';
import classes from '../work-schedule-page.module.scss';
import { ShiftTipsModal } from '../../../components/ShiftTipsModal';
import { formatDateDot } from '../../../utils/date.utils';
import { describeCandidatePriority, describeCandidateConflict } from '../work-schedule-page.utils';
import { getEmployeeLimitWarnings, EMPLOYEE_LIMIT_LABELS } from '../../../utils/employee-limits.utils';
import type { useWorkSchedulePageController } from '../useWorkSchedulePageController';

type ScheduleDialogsProps = Pick<
  ReturnType<typeof useWorkSchedulePageController>,
  | 'employeeInsight'
  | 'user'
  | 'onCloseEmployeeInsight'
  | 'tipDate'
  | 'canManageSchedule'
  | 'setTipDate'
  | 'nameModal'
  | 'setNameModal'
  | 'onNameModalSubmit'
  | 'isSavingModal'
  | 'slotModal'
  | 'onCloseSlotModal'
  | 'onSlotModalSubmit'
  | 'setSlotModal'
  | 'canAssignEmployees'
  | 'createSlotEmployees'
  | 'slotCandidatesSearch'
  | 'setSlotCandidatesSearch'
  | 'isLoadingSlotCandidates'
  | 'filteredSlotCandidates'
  | 'isBusy'
  | 'onDeleteSlot'
>;

export const ScheduleDialogs: React.FC<ScheduleDialogsProps> = ({
  employeeInsight,
  user,
  onCloseEmployeeInsight,
  tipDate,
  canManageSchedule,
  setTipDate,
  nameModal,
  setNameModal,
  onNameModalSubmit,
  isSavingModal,
  slotModal,
  onCloseSlotModal,
  onSlotModalSubmit,
  setSlotModal,
  canAssignEmployees,
  createSlotEmployees,
  slotCandidatesSearch,
  setSlotCandidatesSearch,
  isLoadingSlotCandidates,
  filteredSlotCandidates,
  isBusy,
  onDeleteSlot,
}) => (
  <>
    {employeeInsight && user?.role === 'manager' && (
      <div className={`modalOverlay ${classes.employeeInsightOverlay}`} dir="rtl">
        <aside
          className={classes.employeeInsightPanel}
          role="dialog"
          aria-modal="true"
          aria-labelledby="employee-insight-title"
        >
          <header className={classes.employeeInsightHeader}>
            <div>
              <h2 id="employee-insight-title">{employeeInsight.employee.fullName}</h2>
              {employeeInsight.employee.phoneNumber && (
                <a dir="ltr" href={`tel:${employeeInsight.employee.phoneNumber}`}>
                  {employeeInsight.employee.phoneNumber}
                </a>
              )}
            </div>
            <span
              className={`${classes.employeeInsightButton} ${classes.static} ${employeeInsight.hasWeeklyNote ? classes.hasNote : classes.noNote}`}
            >
              i
            </span>
            <button
              type="button"
              className="modalClose"
              aria-label="סגירת פרטי עובד"
              onClick={onCloseEmployeeInsight}
            >
              ×
            </button>
          </header>

          <div className={classes.employeeInsightCounts}>
            <span>
              <strong>{employeeInsight.requestedDays}</strong> ימים ביקש
            </span>
            <span>
              <strong>{employeeInsight.receivedDays}</strong> ימים קיבל
            </span>
          </div>

          <div className={classes.employeeInsightLegend} aria-label="מקרא">
            <span>
              <b className={classes.requestedMark}>✓</b> ביקש / רוצה
            </span>
            <span>
              <b className={classes.assignedMark}>✓</b> קיבל
            </span>
            <span>
              <b className={classes.vacationMark}>○</b> יום חופש
            </span>
          </div>

          <div className={classes.employeeInsightMatrixWrap}>
            <table role="table" className={`mobileRecordTable ${classes.employeeInsightMatrix}`}>
              <thead>
                <tr>
                  <th scope="col">משמרת</th>
                  {employeeInsight.days.map((day, index) => (
                    <th
                      scope="col"
                      key={day.dateDay}
                      className={day.isApprovedVacation ? classes.vacationDay : ''}
                    >
                      <span>{["א'", "ב'", "ג'", "ד'", "ה'", "ו'", "ש'"][index]}</span>
                      {day.isApprovedVacation && <small>חופש</small>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employeeInsight.shifts.map((shift) => (
                  <tr key={shift.shiftName}>
                    <th scope="row">{shift.shiftName}</th>
                    {shift.days.map((day) => (
                      <td key={day.dateDay} data-label={`${day.dateDay}${employeeInsight.days.find((item) => item.dateDay === day.dateDay)?.isApprovedVacation ? ' · חופש' : ''}`}>
                        {day.requested && (
                          <span className={classes.requestedMark} title="ביקש">
                            ✓
                          </span>
                        )}
                        {day.assigned && (
                          <span className={classes.assignedMark} title="קיבל">
                            ✓
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <section className={classes.employeeInsightNote}>
            <h3>הערה לשבוע</h3>
            <p>{employeeInsight.weeklyNote ?? 'לא נשמרה הערה לשבוע זה'}</p>
          </section>
        </aside>
      </div>
    )}
    {tipDate && canManageSchedule && <ShiftTipsModal date={tipDate} onClose={() => setTipDate(null)} />}
    {nameModal && canManageSchedule && (
      <div className="modalOverlay" dir="rtl">
        <section className={`modalCard ${classes.scheduleModalCard}`}>
          <div className="modalHeader">
            <h2>{nameModal.title}</h2>
            <button type="button" className="modalClose" onClick={() => setNameModal(null)}>
              ×
            </button>
          </div>
          <form className="form" onSubmit={onNameModalSubmit}>
            <label className="label" htmlFor="schedule-name-input">
              שם
            </label>
            <input
              id="schedule-name-input"
              className="input"
              value={nameModal.value}
              onChange={(event) =>
                setNameModal((current) => (current ? { ...current, value: event.target.value } : current))
              }
              placeholder="הזינו שם"
              autoFocus
            />
            {nameModal.minimumTip !== undefined && <label className="label">מינימום טיפ יומי לעובד במחלקה
              <input type="number" className="input" min={0} step={1} value={nameModal.minimumTip}
                disabled={isSavingModal || nameModal.tipConflict} onContextMenu={event => event.preventDefault()} onSelect={event => event.preventDefault()}
                style={{ userSelect: 'none', WebkitTouchCallout: 'none' }}
                onChange={event => { const value = event.currentTarget.value; setNameModal(current => current ? { ...current, minimumTip: value } : current); }} />
            </label>}
            {nameModal.tipConflict && <p role="alert" className="errorText">מינימום הטיפ השתנה במקביל. יש לפתוח מחדש את העריכה.</p>}
            <div className="modalActions">
              <button type="button" className="navArrowButton" onClick={() => setNameModal(null)}>
                ביטול
              </button>
              <button className="button" type="submit" disabled={isSavingModal || nameModal.tipConflict}>
                {isSavingModal ? 'שומר...' : nameModal.submitLabel}
              </button>
            </div>
          </form>
        </section>
      </div>
    )}
    {slotModal && (
      <div className="modalOverlay" dir="rtl">
        <section className={`modalCard ${classes.scheduleModalCard}`}>
          <div className="modalHeader">
            <h2>{slotModal.mode === 'create' ? 'יצירת סלוט' : 'עריכת סלוט'}</h2>
            <button type="button" className="modalClose" onClick={onCloseSlotModal}>
              ×
            </button>
          </div>
          <form className="form" onSubmit={onSlotModalSubmit}>
            <p className={classes.slotDateHint}>תאריך: {formatDateDot(slotModal.dateDay)}</p>
            <label className="label" htmlFor="slot-start-hour">
              שעת התחלה
            </label>
            <input
              id="slot-start-hour"
              className="input"
              type="time"
              value={slotModal.startHour}
              onChange={(event) =>
                setSlotModal((current) => (current ? { ...current, startHour: event.target.value } : current))
              }
              required
              disabled={!canManageSchedule}
            />
            <label className="label" htmlFor="slot-end-hour">
              שעת סיום
            </label>
            <input
              id="slot-end-hour"
              className="input"
              type="time"
              value={slotModal.endHour}
              onChange={(event) =>
                setSlotModal((current) => (current ? { ...current, endHour: event.target.value } : current))
              }
              required
              disabled={!canManageSchedule}
            />

            {slotModal.mode === 'create' && canAssignEmployees && (
              <label className="label" htmlFor="slot-create-employee">
                עובד
                <select
                  id="slot-create-employee"
                  className="input"
                  value={slotModal.employeePk ?? ''}
                  onChange={(event) =>
                    setSlotModal((current) =>
                      current
                        ? {
                            ...current,
                            employeePk: event.target.value ? Number(event.target.value) : null,
                          }
                        : current,
                    )
                  }
                >
                  <option value="">ללא שיבוץ</option>
                  {createSlotEmployees.map((employee) => (
                    <option key={employee.employeePk} value={employee.employeePk}>
                      {[employee.firstName, employee.lastName].filter(Boolean).join(' ')}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {slotModal.mode === 'edit' && canAssignEmployees && (
              <div className={classes.slotAssignmentPanel}>
                <div className={classes.slotAssignmentHeader}>
                  <h3 className={classes.slotAssignmentTitle}>שיבוץ עובד</h3>
                  <button
                    type="button"
                    className="navArrowButton"
                    onClick={() =>
                      setSlotModal((current) => (current ? { ...current, employeePk: null } : current))
                    }
                    disabled={slotModal.employeePk === null}
                  >
                    הסר שיבוץ
                  </button>
                </div>

                <p className={classes.slotAssignmentCurrent}>
                  שיבוץ נוכחי: {slotModal.currentEmployeeName ?? 'לא שובץ עובד'}
                </p>

                <input
                  className={`input ${classes.slotCandidateSearchInput}`}
                  placeholder="חיפוש עובד לפי שם פרטי או משפחה"
                  value={slotCandidatesSearch}
                  onChange={(event) => setSlotCandidatesSearch(event.target.value)}
                />

                {isLoadingSlotCandidates && (
                  <p className={classes.slotCandidatesHint}>טוען מועמדים לשיבוץ...</p>
                )}

                {!isLoadingSlotCandidates && filteredSlotCandidates.length === 0 && (
                  <p className={classes.slotCandidatesHint}>לא נמצאו עובדים התואמים לחיפוש.</p>
                )}

                {!isLoadingSlotCandidates && filteredSlotCandidates.length > 0 && (
                  <div className={classes.slotCandidatesList}>
                    {filteredSlotCandidates.map((candidate) => {
                      const conflictHint = describeCandidateConflict(candidate);
                      const isSelected = slotModal.employeePk === candidate.employeePk;
                      const limitWarnings = candidate.weeklyLimits ? getEmployeeLimitWarnings(candidate.weeklyLimits.limits,candidate.weeklyLimits.counts) : [];

                      return (
                        <button
                          key={candidate.employeePk}
                          type="button"
                          className={`${classes.slotCandidateOption} ${isSelected ? classes.selected : ''}`}
                          disabled={candidate.isRoleEligible === false}
                          onClick={() =>
                            setSlotModal((current) =>
                              current ? { ...current, employeePk: candidate.employeePk } : current,
                            )
                          }
                        >
                          <div className={classes.slotCandidateTopRow}>
                            <p className={classes.slotCandidateName}>{candidate.fullName}</p>
                            <span className={classes.slotCandidatePriority}>
                              {describeCandidatePriority(candidate)}
                            </span>
                          </div>

                          <div className={classes.slotCandidateBadges}>
                            {candidate.schedulingPriority > 0 && <span className={`${classes.slotBadge} ${classes.neutral}`}>עדיפות: {candidate.schedulingPriority}</span>}
                            <span
                              className={`${classes.slotBadge} ${candidate.isAvailable ? classes.good : classes.warn}`}
                            >
                              {candidate.isAvailable ? 'זמין' : 'לא זמין'}
                            </span>
                            <span
                              className={`${classes.slotBadge} ${candidate.sameSection ? classes.neutral : classes.soft}`}
                            >
                              {candidate.sameSection ? 'שייך למחלקה' : 'מחלקה אחרת'}
                            </span>
                            {candidate.hasOtherSlotSameDay && (
                              <span className={`${classes.slotBadge} ${classes.conflict}`}>
                                משובץ כבר היום
                              </span>
                            )}
                            {!candidate.hasShiftUser && (
                              <span className={`${classes.slotBadge} ${classes.subtle}`}>
                                ללא משתמש SHIFT
                              </span>
                            )}
                          </div>

                          {conflictHint && (
                            <p className={classes.slotCandidateConflictHint}>{conflictHint}</p>
                          )}
                          {limitWarnings.length > 0 && <p className={classes.slotCandidateConflictHint}>
                            {limitWarnings.map(warning => `${EMPLOYEE_LIMIT_LABELS[warning.key]}: ${warning.actual} / ${warning.limit}`).join(' · ')}
                          </p>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="modalActions">
              <button type="button" className="navArrowButton" onClick={onCloseSlotModal}>
                ביטול
              </button>
              {slotModal.mode === 'edit' && canManageSchedule && slotModal.slotId && (
                <button
                  type="button"
                  className={`${classes.slotActionButton} ${classes.danger}`}
                  disabled={isBusy(`delete-slot-${slotModal.slotId}`)}
                  onClick={() => {
                    void onDeleteSlot(slotModal.slotId as number).then(
                      (deleted) => deleted && onCloseSlotModal(),
                    );
                  }}
                >
                  מחיקת סלוט
                </button>
              )}
              <button className="button" type="submit" disabled={isSavingModal}>
                {isSavingModal ? 'שומר...' : slotModal.mode === 'create' ? 'יצירת סלוט' : 'שמירה'}
              </button>
            </div>
          </form>
        </section>
      </div>
    )}
  </>
);
